import { useEffect, useRef, useState, useMemo } from "react";
import { createChart, CandlestickSeries } from "lightweight-charts";

// TradingView-quality candlestick chart using their official lightweight-charts lib.
// Supports zoom (pinch/scroll), horizontal scroll (drag), 10 timeframes, live 3s updates,
// synthetic history seeded from admin base price with per-TF localStorage persistence.
export default function LiveCandleChart({ liveInfo, cfg, height = 380 }) {
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const seriesRef = useRef(null);
  const ticksRef = useRef([]);
  const [tfKey, setTfKey] = useState("1m");
  const [lastPrice, setLastPrice] = useState(0);
  const [priceDir, setPriceDir] = useState(0);

  const TIMEFRAMES = useMemo(() => ({
    "1m":  { sec: 60,               label: "1m",  count: 120 },
    "3m":  { sec: 180,              label: "3m",  count: 120 },
    "5m":  { sec: 300,              label: "5m",  count: 120 },
    "15m": { sec: 900,              label: "15m", count: 120 },
    "30m": { sec: 1800,             label: "30m", count: 120 },
    "1h":  { sec: 3600,             label: "1H",  count: 120 },
    "4h":  { sec: 14400,            label: "4H",  count: 120 },
    "1d":  { sec: 86400,            label: "1D",  count: 90  },
    "7d":  { sec: 604800,           label: "1W",  count: 60  },
    "30d": { sec: 2592000,          label: "1M",  count: 24  },
  }), []);

  // Init chart
  useEffect(() => {
    if (!containerRef.current) return;
    const chart = createChart(containerRef.current, {
      layout: {
        background: { color: "transparent" },
        textColor: "#94A3B8",
        fontFamily: "ui-monospace, Menlo, Monaco, monospace",
      },
      localization: {
        locale: "en-US",
        dateFormat: "yyyy-MM-dd",
      },
      grid: {
        vertLines: { color: "rgba(255,255,255,0.05)" },
        horzLines: { color: "rgba(255,255,255,0.05)" },
      },
      rightPriceScale: {
        borderColor: "rgba(255,255,255,0.08)",
      },
      timeScale: {
        borderColor: "rgba(255,255,255,0.08)",
        timeVisible: true,
        secondsVisible: false,
        tickMarkFormatter: (time) => {
          try {
            const d = new Date(time * 1000);
            return new Intl.DateTimeFormat("en-US", { month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" }).format(d);
          } catch {
            return "";
          }
        },
      },
      crosshair: {
        mode: 1,
        vertLine: { color: "rgba(234,179,8,0.4)", width: 1, style: 3 },
        horzLine: { color: "rgba(234,179,8,0.4)", width: 1, style: 3 },
      },
      autoSize: true,
      handleScroll: true,
      handleScale: true,
    });
    const series = chart.addSeries(CandlestickSeries, {
      upColor: "#22C55E",
      downColor: "#EF4444",
      borderUpColor: "#22C55E",
      borderDownColor: "#EF4444",
      wickUpColor: "#22C55E",
      wickDownColor: "#EF4444",
    });
    chartRef.current = chart;
    seriesRef.current = series;
    const ro = new ResizeObserver(() => chart.applyOptions({}));
    ro.observe(containerRef.current);
    return () => { ro.disconnect(); chart.remove(); chartRef.current = null; seriesRef.current = null; };
  }, []);

  // Build candles for selected TF (seed synthetic history + live overlay)
  useEffect(() => {
    if (!liveInfo || !seriesRef.current) return;
    const base = Number(liveInfo.base_price);
    const vpct = Number(liveInfo.variance_pct) / 100;
    const tf = TIMEFRAMES[tfKey];
    const key = `nbt_lw_candles_${tfKey}_v1`;
    let candles;
    try { candles = JSON.parse(localStorage.getItem(key) || "null"); } catch { candles = null; }
    if (!candles || !Array.isArray(candles) || candles.length !== tf.count) {
      candles = [];
      let prev = base * (1 + (Math.random() - 0.5) * vpct);
      const now = Math.floor(Date.now() / 1000);
      const start = Math.floor(now / tf.sec) * tf.sec - (tf.count - 1) * tf.sec;
      for (let i = 0; i < tf.count; i++) {
        const t = start + i * tf.sec;
        const o = prev;
        let h = o, l = o, c = o;
        for (let j = 0; j < 14; j++) {
          const step = (Math.random() - 0.5) * base * vpct * 0.4;
          c = Math.min(base * (1 + vpct * 1.2), Math.max(base * (1 - vpct * 1.2), c + step));
          if (c > h) h = c;
          if (c < l) l = c;
        }
        candles.push({ time: t, open: o, high: h, low: l, close: c });
        prev = c;
      }
      try { localStorage.setItem(key, JSON.stringify(candles)); } catch {}
    }
    seriesRef.current.setData(candles);
    chartRef.current?.timeScale().fitContent();
    setLastPrice(candles[candles.length - 1].close);
  }, [tfKey, liveInfo, TIMEFRAMES]);

  // Live 3s tick loop — random walk + update the most recent candle
  useEffect(() => {
    if (!liveInfo || !seriesRef.current) return;
    const base = Number(liveInfo.base_price);
    const vpct = Number(liveInfo.variance_pct) / 100;
    const tf = TIMEFRAMES[tfKey];
    const iv = setInterval(() => {
      const now = Math.floor(Date.now() / 1000);
      const bucket = Math.floor(now / tf.sec) * tf.sec;
      const key = `nbt_lw_candles_${tfKey}_v1`;
      let candles;
      try { candles = JSON.parse(localStorage.getItem(key) || "null"); } catch { candles = null; }
      if (!candles) return;
      let cur = candles[candles.length - 1];
      // Random walk step
      const step = (Math.random() - 0.5) * base * vpct * 0.35;
      const newPrice = Math.min(base * (1 + vpct * 1.2), Math.max(base * (1 - vpct * 1.2), cur.close + step));
      setPriceDir(newPrice > cur.close ? 1 : newPrice < cur.close ? -1 : 0);
      setLastPrice(newPrice);
      if (cur.time === bucket) {
        cur.close = newPrice;
        if (newPrice > cur.high) cur.high = newPrice;
        if (newPrice < cur.low) cur.low = newPrice;
        seriesRef.current.update({ time: cur.time, open: cur.open, high: cur.high, low: cur.low, close: cur.close });
      } else {
        const newCandle = { time: bucket, open: cur.close, high: newPrice, low: Math.min(cur.close, newPrice), close: newPrice };
        candles.push(newCandle);
        if (candles.length > tf.count + 20) candles.shift();
        seriesRef.current.update(newCandle);
      }
      try { localStorage.setItem(key, JSON.stringify(candles)); } catch {}
    }, Number(liveInfo.tick_interval_sec || 3) * 1000);
    return () => clearInterval(iv);
  }, [tfKey, liveInfo, TIMEFRAMES]);

  return (
    <div>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <div className="font-heading text-white text-sm">Live NBT/USDT</div>
          <span className={`inline-flex items-center gap-1 text-xs font-mono px-2 py-0.5 rounded border ${priceDir >= 0 ? "border-[#22C55E]/40 text-[#22C55E] bg-[#22C55E]/5" : "border-red-500/40 text-red-400 bg-red-500/5"}`}>
            <span className={`h-1.5 w-1.5 rounded-full ${priceDir >= 0 ? "bg-[#22C55E]" : "bg-red-500"} animate-pulse`}></span>
            ${lastPrice.toFixed(4)}
          </span>
        </div>
        <div className="flex gap-1 flex-wrap">
          {Object.entries(TIMEFRAMES).map(([k, tf]) => (
            <button key={k} data-testid={`nb-tf-${k}`} onClick={() => setTfKey(k)}
              className={`px-2 py-1 rounded text-[10px] font-mono border transition-colors ${tfKey === k ? "border-[#EAB308]/50 text-[#EAB308] bg-[#EAB308]/10" : "border-white/10 text-slate-400 hover:text-white"}`}>
              {tf.label}
            </button>
          ))}
        </div>
      </div>
      <div ref={containerRef} style={{ width: "100%", height }} data-testid="nb-lw-chart"/>
      <div className="mt-2 text-[10px] text-slate-500 text-center uppercase tracking-widest">Powered by TradingView Lightweight Charts · pinch/scroll to zoom · drag to pan · updates every 3s</div>
    </div>
  );
}
